import { HugeiconsIcon } from "@hugeicons/react";
import { ShoppingCart01Icon } from "@hugeicons-pro/core-bulk-rounded";

import { PageHeader } from "@/components/layout/page-header";
import { GlobalFilterBar } from "@/components/filters/global-filter-bar";
import { MetricTiles } from "@/components/dashboard/metric-tiles";
import { ChartCard } from "@/components/dashboard/chart-card";
import { TrendArea, CategoryBars } from "@/components/dashboard/charts";
import { BreakdownTable } from "@/components/dashboard/breakdown-table";
import { labelCol, platformLabelCol, spendCol, revenueCol, roasCol, merCol, ordersCol, aovCol, cvrCol } from "@/components/dashboard/columns";
import { Skeleton } from "@/components/ui/skeleton";
import { FadeIn } from "@/components/ui/motion";
import { seriesColor } from "@/lib/chart-theme";
import { useTotals, useSeries, useBreakdown } from "@/lib/use-metrics";
import { useReference, channelRows } from "@/lib/use-reference";
import { useMetricMoney } from "@/lib/use-money";

export function Ecom() {
  const { metrics, platforms } = useReference();
  const m = useMetricMoney();
  // Blended, not ecom-only. Awareness spend in a market also drives the sales in
  // that market, so reporting ecom-activity spend against total revenue overstated
  // efficiency and made this view impossible to reconcile with Converge, which
  // blends every campaign. All four reads therefore span every activity.
  const totals = useTotals("all");
  const series = useSeries("all");
  const byPlatform = useBreakdown("platform", "all");
  const byBrand = useBreakdown("brand", "all");

  const loading = totals.isPending;
  const platformRows = channelRows(byPlatform.data ?? [], platforms);

  const platformColumns = [
    platformLabelCol("Platform", platforms), spendCol(m.fmt), revenueCol(m.fmt), roasCol(), merCol(), ordersCol(), aovCol(m.fmt), cvrCol(),
  ];

  const brandBars = (byBrand.data ?? []).map((r) => ({ label: r.label, revenue: r.revenue }));

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="E-commerce"
        icon={ShoppingCart01Icon}
        title="E-commerce performance"
        description="Blended omzet, spend en rendement over álle campagnes in de gekozen markt — per kanaal, merk en markt."
        tabs={<GlobalFilterBar showActivity={false} activity="ecom" />}
      />

      {loading ? (
        <Skeleton className="h-44 rounded-2xl" />
      ) : (
        <MetricTiles
          keys={["revenue", "roas", "mer", "orders", "conversion_rate", "new_customers", "cpa_new_customer", "spend"]}
          values={totals.data!.kpis as unknown as Record<string, number | null>}
          prev={totals.data!.prev as unknown as Record<string, number | null>}
          metrics={metrics}
          money={m.fmt}
        />
      )}

      <FadeIn delay={0.05}>
        <div className="grid gap-4 lg:grid-cols-2">
          <ChartCard title="Omzet & uitgaven" subtitle="Dagelijks" bodyClassName="h-80">
            <TrendArea
              data={series.data ?? []}
              moneyFmt={m.fmt}
              series={[
                { dataKey: "revenue", name: "Omzet", color: seriesColor("revenue"), kind: "currency", axis: "left" },
                { dataKey: "spend", name: "Uitgaven", color: seriesColor("spend"), kind: "currency", axis: "right" },
              ]}
            />
          </ChartCard>
          <ChartCard title="Omzet per merk" subtitle="Totaal over de periode" bodyClassName="h-80">
            <CategoryBars data={brandBars} dataKey="revenue" kind="currency" moneyFmt={m.fmt} />
          </ChartCard>
        </div>
      </FadeIn>

      <FadeIn delay={0.1}>
        <BreakdownTable title="Per channel" description="Ecom-uitgaven, omzet en rendement per media-channel over de gekozen periode." rows={platformRows} columns={platformColumns} rowKey={(r) => r.key} exportName="Prado Pulse — E-commerce per channel" />
      </FadeIn>
    </div>
  );
}
